import { z } from "zod";

import {
  analysisSnapshotSchema,
  halfLifeResultSchema,
  analysisSnapshotsQuerySchema,
  dailySettlementSchema,
  dataAvailabilitySchema,
  dataCoverageSchema,
  futuresContractSchema,
  fxRateSchema,
  fxRatesQuerySchema,
  isSyntheticSourceId,
  listContractsQuerySchema,
  statisticalTestResultSchema,
  settlementsQuerySchema,
  type AnalysisSnapshot,
  type AnalysisSnapshotsQuery,
  type DailySettlement,
  type DataCoverage,
  type FuturesContract,
  type FxRate,
  type FxRatesQuery,
  type ListContractsQuery,
  type SettlementsQuery,
} from "../contracts/schemas";
import { AdapterConfigurationError, AdapterDatabaseError } from "../adapters/errors";
import type { MarketDataAdapter } from "../adapters/market-data-adapter";
import type {
  D1DatabaseLike,
  D1PreparedStatementLike,
  D1ResultLike,
  D1Value,
} from "./d1-types";

const unknownRecordSchema = z.record(z.string(), z.unknown());
export const D1_MAX_BOUND_PARAMETERS = 100;

export interface D1MarketDataAdapterConfig {
  sourceId?: string;
}

export class D1MarketDataAdapter implements MarketDataAdapter {
  readonly mode = "production" as const;

  private readonly sourceId: string;

  constructor(
    private readonly database: D1DatabaseLike,
    config: D1MarketDataAdapterConfig = {},
  ) {
    const sourceId = config.sourceId?.trim() || "cloudflare-d1";
    if (isSyntheticSourceId(sourceId)) {
      throw new AdapterConfigurationError(
        "A production D1 adapter cannot use the synthetic fixture source identity",
      );
    }
    this.sourceId = sourceId;
  }

  async listContracts(query: ListContractsQuery = {}): Promise<FuturesContract[]> {
    const parsed = listContractsQuerySchema.parse(query);
    const clauses: string[] = ["1 = 1"];
    const values: D1Value[] = [];

    if (parsed.instrumentIds?.length) {
      clauses.push(`instrument_id IN (${placeholders(parsed.instrumentIds.length)})`);
      values.push(...parsed.instrumentIds);
    }
    if (parsed.deliveryYearFrom !== undefined) {
      clauses.push("delivery_year >= ?");
      values.push(parsed.deliveryYearFrom);
    }
    if (parsed.deliveryYearTo !== undefined) {
      clauses.push("delivery_year <= ?");
      values.push(parsed.deliveryYearTo);
    }
    if (parsed.enabledOnly) clauses.push("enabled = 1");
    assertD1BindLimit(values.length);

    const statement = this.database
      .prepare(
        `SELECT
          id,
          instrument_id AS instrumentId,
          month_code AS monthCode,
          delivery_year AS deliveryYear,
          delivery_month AS deliveryMonth,
          first_notice_date AS firstNoticeDate,
          last_trade_date AS lastTradeDate,
          expiry_date AS expiryDate,
          crop_year AS cropYear,
          crop_phase AS cropPhase,
          enabled,
          data_availability_json AS dataAvailabilityJson,
          liquidity_metadata_json AS liquidityMetadataJson
        FROM futures_contracts
        WHERE ${clauses.join(" AND ")}
        ORDER BY instrument_id, delivery_year, delivery_month`,
      )
      .bind(...values);

    const rows = await allRows(statement, "list futures contracts");
    return rows.map(mapContractRow);
  }

  async getSettlements(query: SettlementsQuery): Promise<DailySettlement[]> {
    const parsed = settlementsQuerySchema.parse(query);
    const clauses = [
      `contract_id IN (${placeholders(parsed.contractIds.length)})`,
      "exchange_business_date >= ?",
      "exchange_business_date <= ?",
    ];
    const values: D1Value[] = [...parsed.contractIds, parsed.startDate, parsed.endDate];

    if (parsed.asOfTimestamp !== undefined) {
      clauses.push("julianday(ingested_at) <= julianday(?)");
      values.push(parsed.asOfTimestamp);
    }
    assertD1BindLimit(values.length);

    const statement = this.database
      .prepare(
        `WITH ranked_settlements AS (
          SELECT
            contract_id,
            exchange_business_date,
            settlement_timestamp,
            raw_price,
            normalized_price,
            normalization_state,
            volume,
            open_interest,
            source,
            quality_flags_json,
            ingested_at,
            ROW_NUMBER() OVER (
              PARTITION BY contract_id, exchange_business_date
              ORDER BY julianday(ingested_at) DESC, id DESC
            ) AS revision_rank
          FROM daily_settlements
          WHERE ${clauses.join(" AND ")}
        )
        SELECT
          contract_id AS contractId,
          exchange_business_date AS exchangeBusinessDate,
          settlement_timestamp AS settlementTimestamp,
          raw_price AS rawPrice,
          normalized_price AS normalizedPrice,
          normalization_state AS normalizationState,
          volume,
          open_interest AS openInterest,
          source,
          quality_flags_json AS qualityFlagsJson,
          ingested_at AS ingestedAt
        FROM ranked_settlements
        WHERE revision_rank = 1
        ORDER BY exchange_business_date, contract_id`,
      )
      .bind(...values);

    const rows = await allRows(statement, "read daily settlements");
    const settlements = rows.map(mapSettlementRow);
    assertProductionSources(settlements, this.sourceId, "daily settlement");
    return settlements;
  }

  async getFxRates(query: FxRatesQuery): Promise<FxRate[]> {
    const parsed = fxRatesQuerySchema.parse(query);
    const clauses = [
      `pair IN (${placeholders(parsed.pairs.length)})`,
      "date >= ?",
      "date <= ?",
    ];
    const values: D1Value[] = [...parsed.pairs, parsed.startDate, parsed.endDate];

    if (parsed.asOfTimestamp !== undefined) {
      clauses.push("julianday(ingested_at) <= julianday(?)");
      values.push(parsed.asOfTimestamp);
    }
    assertD1BindLimit(values.length);

    const statement = this.database
      .prepare(
        `WITH ranked_rates AS (
          SELECT
            pair,
            date,
            timestamp,
            rate,
            source,
            quality_flags_json,
            ingested_at,
            ROW_NUMBER() OVER (
              PARTITION BY pair, date
              ORDER BY julianday(ingested_at) DESC, id DESC
            ) AS revision_rank
          FROM fx_rates
          WHERE ${clauses.join(" AND ")}
        )
        SELECT
          pair,
          date,
          timestamp,
          rate,
          source,
          quality_flags_json AS qualityFlagsJson,
          ingested_at AS ingestedAt
        FROM ranked_rates
        WHERE revision_rank = 1
        ORDER BY date, pair`,
      )
      .bind(...values);

    const rows = await allRows(statement, "read FX rates");
    const rates = rows.map(mapFxRateRow);
    assertProductionSources(rates, this.sourceId, "FX rate");
    return rates;
  }

  async getCoverage(): Promise<DataCoverage> {
    const summary = await firstRow(
      this.database.prepare(
        `SELECT
          (SELECT MIN(exchange_business_date) FROM daily_settlements) AS startDate,
          (SELECT MAX(exchange_business_date) FROM daily_settlements) AS endDate,
          (SELECT ingested_at FROM daily_settlements ORDER BY julianday(ingested_at) DESC LIMIT 1) AS lastSettlementUpdate,
          (SELECT ingested_at FROM fx_rates ORDER BY julianday(ingested_at) DESC LIMIT 1) AS lastFxUpdate,
          (SELECT COUNT(*) FROM futures_contracts) AS contractCount,
          (SELECT COUNT(*) FROM daily_settlements) AS settlementCount,
          (SELECT COUNT(*) FROM fx_rates) AS fxRateCount`,
      ),
      "read coverage summary",
    );

    const instruments = await allRows(
      this.database.prepare(
        "SELECT DISTINCT instrument_id AS instrumentId FROM futures_contracts ORDER BY instrument_id",
      ),
      "read coverage instruments",
    );

    const sources = await allRows(
      this.database.prepare(
        `SELECT DISTINCT source FROM (
          SELECT source FROM daily_settlements
          UNION ALL
          SELECT source FROM fx_rates
        ) ORDER BY source`,
      ),
      "read coverage sources",
    );
    assertProductionSources(
      sources.map((row) => ({ source: requiredString(row, "source") })),
      this.sourceId,
      "coverage record",
    );

    const settlementCount = requiredNumber(summary, "settlementCount");
    const lastUpdatedAt = maximumNullableTimestamp(
      nullableString(summary, "lastSettlementUpdate"),
      nullableString(summary, "lastFxUpdate"),
    );

    return dataCoverageSchema.parse({
      sourceId: this.sourceId,
      startDate: nullableString(summary, "startDate"),
      endDate: nullableString(summary, "endDate"),
      lastUpdatedAt,
      instrumentIds: instruments.map((row) => requiredString(row, "instrumentId")),
      contractCount: requiredNumber(summary, "contractCount"),
      settlementCount,
      fxRateCount: requiredNumber(summary, "fxRateCount"),
      qualityStatus: settlementCount > 0 ? "ok" : "unavailable",
    });
  }

  async getAnalysisSnapshots(
    query: AnalysisSnapshotsQuery = {},
  ): Promise<AnalysisSnapshot[]> {
    const parsed = analysisSnapshotsQuerySchema.parse(query);
    const clauses: string[] = ["1 = 1"];
    const values: D1Value[] = [];

    if (parsed.pairPresetIds?.length) {
      clauses.push(`pair_preset_id IN (${placeholders(parsed.pairPresetIds.length)})`);
      values.push(...parsed.pairPresetIds);
    }
    if (parsed.asOfDate !== undefined) {
      clauses.push("as_of_date = ?");
      values.push(parsed.asOfDate);
    }
    if (parsed.analysisConfigId !== undefined) {
      clauses.push("analysis_config_id = ?");
      values.push(parsed.analysisConfigId);
    }
    assertD1BindLimit(values.length);

    const statement = this.database
      .prepare(
        `SELECT
          id,
          pair_preset_id AS pairPresetId,
          leg_a_contract_id AS legAContractId,
          leg_b_contract_id AS legBContractId,
          as_of_date AS asOfDate,
          analysis_config_id AS analysisConfigId,
          analysis_config_version AS analysisConfigVersion,
          raw_spread AS rawSpread,
          local_z AS localZ,
          seasonal_z AS seasonalZ,
          return_correlation AS returnCorrelation,
          beta,
          adf_result_json AS adfResultJson,
          cointegration_result_json AS cointegrationResultJson,
          half_life_result_json AS halfLifeResultJson,
          sample_size AS sampleSize,
          quality_status AS qualityStatus,
          alignment_rule AS alignmentRule,
          input_data_as_of_timestamp AS inputDataAsOfTimestamp,
          model_parameters_as_of_timestamp AS modelParametersAsOfTimestamp,
          result_json AS resultJson,
          generated_at AS generatedAt
        FROM analysis_snapshots
        WHERE ${clauses.join(" AND ")}
        ORDER BY as_of_date DESC, id`,
      )
      .bind(...values);

    const rows = await allRows(statement, "read analysis snapshots");
    return rows.map(mapAnalysisSnapshotRow);
  }

  async saveAnalysisSnapshot(snapshot: AnalysisSnapshot): Promise<AnalysisSnapshot> {
    const parsed = analysisSnapshotSchema.parse(snapshot);
    const config = await firstRow(
      this.database
        .prepare(
          `SELECT minimum_sample AS minimumSample
          FROM analysis_configs
          WHERE id = ? AND version = ?`,
        )
        .bind(parsed.analysisConfigId, parsed.analysisConfigVersion),
      "read analysis config for snapshot",
    );
    assertSnapshotHasSufficientSample(parsed, requiredNumber(config, "minimumSample"));
    const result = await this.database
      .prepare(
        `INSERT INTO analysis_snapshots (
          id,
          pair_preset_id,
          leg_a_contract_id,
          leg_b_contract_id,
          as_of_date,
          analysis_config_id,
          analysis_config_version,
          raw_spread,
          local_z,
          seasonal_z,
          return_correlation,
          beta,
          adf_result_json,
          cointegration_result_json,
          half_life_result_json,
          sample_size,
          quality_status,
          alignment_rule,
          input_data_as_of_timestamp,
          model_parameters_as_of_timestamp,
          result_json,
          generated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)
        ON CONFLICT (
          pair_preset_id,
          leg_a_contract_id,
          leg_b_contract_id,
          as_of_date,
          analysis_config_id,
          analysis_config_version
        ) DO UPDATE SET
          id = excluded.id,
          raw_spread = excluded.raw_spread,
          local_z = excluded.local_z,
          seasonal_z = excluded.seasonal_z,
          return_correlation = excluded.return_correlation,
          beta = excluded.beta,
          adf_result_json = excluded.adf_result_json,
          cointegration_result_json = excluded.cointegration_result_json,
          half_life_result_json = excluded.half_life_result_json,
          sample_size = excluded.sample_size,
          quality_status = excluded.quality_status,
          alignment_rule = excluded.alignment_rule,
          input_data_as_of_timestamp = excluded.input_data_as_of_timestamp,
          model_parameters_as_of_timestamp = excluded.model_parameters_as_of_timestamp,
          result_json = excluded.result_json,
          generated_at = excluded.generated_at`,
      )
      .bind(
        parsed.id,
        parsed.pairPresetId,
        parsed.contracts.legAContractId,
        parsed.contracts.legBContractId,
        parsed.asOfDate,
        parsed.analysisConfigId,
        parsed.analysisConfigVersion,
        parsed.rawSpread,
        parsed.localZ,
        parsed.seasonalZ,
        parsed.returnCorrelation,
        parsed.beta,
        JSON.stringify(parsed.adf),
        JSON.stringify(parsed.cointegration),
        JSON.stringify(parsed.halfLife),
        parsed.sampleSize,
        parsed.qualityStatus,
        parsed.alignmentRule,
        parsed.inputDataAsOfTimestamp,
        parsed.modelParametersAsOfTimestamp,
        JSON.stringify(parsed.result),
        parsed.generatedAt,
      )
      .run();

    assertD1Success(result, "save analysis snapshot");
    return parsed;
  }
}

export function createD1MarketDataAdapter(
  database: D1DatabaseLike | undefined,
  config: D1MarketDataAdapterConfig = {},
): D1MarketDataAdapter {
  if (!database) {
    throw new AdapterConfigurationError(
      "Cloudflare D1 binding is not configured; demo fallback is disabled",
    );
  }
  return new D1MarketDataAdapter(database, config);
}

function placeholders(count: number): string {
  return Array.from({ length: count }, () => "?").join(", ");
}

async function allRows(
  statement: D1PreparedStatementLike,
  operation: string,
): Promise<Array<Record<string, unknown>>> {
  let result: D1ResultLike<Record<string, unknown>>;
  try {
    result = await statement.all<Record<string, unknown>>();
  } catch (error) {
    throw new AdapterDatabaseError(`Could not ${operation}`, { cause: error });
  }
  assertD1Success(result, operation);
  return result.results ?? [];
}

async function firstRow(
  statement: D1PreparedStatementLike,
  operation: string,
): Promise<Record<string, unknown>> {
  try {
    const row = await statement.first<Record<string, unknown>>();
    if (!row) throw new AdapterDatabaseError(`Could not ${operation}: no row returned`);
    return row;
  } catch (error) {
    if (error instanceof AdapterDatabaseError) throw error;
    throw new AdapterDatabaseError(`Could not ${operation}`, { cause: error });
  }
}

function assertD1Success(result: D1ResultLike, operation: string): void {
  if (!result.success) {
    throw new AdapterDatabaseError(
      `Could not ${operation}${result.error ? `: ${result.error}` : ""}`,
    );
  }
}

function mapContractRow(row: Record<string, unknown>): FuturesContract {
  return futuresContractSchema.parse({
    id: requiredString(row, "id"),
    instrumentId: requiredString(row, "instrumentId"),
    monthCode: requiredString(row, "monthCode"),
    deliveryYear: requiredNumber(row, "deliveryYear"),
    deliveryMonth: requiredNumber(row, "deliveryMonth"),
    firstNoticeDate: nullableString(row, "firstNoticeDate"),
    lastTradeDate: nullableString(row, "lastTradeDate"),
    expiryDate: nullableString(row, "expiryDate"),
    cropYear: nullableString(row, "cropYear"),
    cropPhase: nullableString(row, "cropPhase"),
    enabled: requiredBoolean(row, "enabled"),
    dataAvailability: parseJson(
      row.dataAvailabilityJson,
      dataAvailabilitySchema,
      "dataAvailabilityJson",
    ),
    liquidityMetadata: parseJson(
      row.liquidityMetadataJson,
      unknownRecordSchema,
      "liquidityMetadataJson",
    ),
  });
}

function mapSettlementRow(row: Record<string, unknown>): DailySettlement {
  return dailySettlementSchema.parse({
    contractId: requiredString(row, "contractId"),
    exchangeBusinessDate: requiredString(row, "exchangeBusinessDate"),
    settlementTimestamp: requiredString(row, "settlementTimestamp"),
    rawPrice: requiredNumber(row, "rawPrice"),
    normalizedPrice: nullableNumber(row, "normalizedPrice"),
    normalizationState: requiredString(row, "normalizationState"),
    volume: nullableNumber(row, "volume"),
    openInterest: nullableNumber(row, "openInterest"),
    source: requiredString(row, "source"),
    qualityFlags: parseJson(row.qualityFlagsJson, z.array(z.string()), "qualityFlagsJson"),
    ingestedAt: requiredString(row, "ingestedAt"),
  });
}

function mapFxRateRow(row: Record<string, unknown>): FxRate {
  return fxRateSchema.parse({
    pair: requiredString(row, "pair"),
    date: requiredString(row, "date"),
    timestamp: requiredString(row, "timestamp"),
    rate: requiredNumber(row, "rate"),
    source: requiredString(row, "source"),
    qualityFlags: parseJson(row.qualityFlagsJson, z.array(z.string()), "qualityFlagsJson"),
    ingestedAt: requiredString(row, "ingestedAt"),
  });
}

function mapAnalysisSnapshotRow(row: Record<string, unknown>): AnalysisSnapshot {
  return analysisSnapshotSchema.parse({
    id: requiredString(row, "id"),
    pairPresetId: requiredString(row, "pairPresetId"),
    contracts: {
      legAContractId: requiredString(row, "legAContractId"),
      legBContractId: requiredString(row, "legBContractId"),
    },
    asOfDate: requiredString(row, "asOfDate"),
    analysisConfigId: requiredString(row, "analysisConfigId"),
    analysisConfigVersion: requiredString(row, "analysisConfigVersion"),
    rawSpread: nullableNumber(row, "rawSpread"),
    localZ: nullableNumber(row, "localZ"),
    seasonalZ: nullableNumber(row, "seasonalZ"),
    returnCorrelation: nullableNumber(row, "returnCorrelation"),
    beta: nullableNumber(row, "beta"),
    adf: parseJson(row.adfResultJson, statisticalTestResultSchema, "adfResultJson"),
    cointegration: parseJson(
      row.cointegrationResultJson,
      statisticalTestResultSchema,
      "cointegrationResultJson",
    ),
    halfLife: parseJson(
      row.halfLifeResultJson,
      halfLifeResultSchema,
      "halfLifeResultJson",
    ),
    sampleSize: requiredNumber(row, "sampleSize"),
    qualityStatus: requiredString(row, "qualityStatus"),
    alignmentRule: requiredString(row, "alignmentRule"),
    inputDataAsOfTimestamp: requiredString(row, "inputDataAsOfTimestamp"),
    modelParametersAsOfTimestamp: requiredString(row, "modelParametersAsOfTimestamp"),
    result: parseJson(row.resultJson, unknownRecordSchema, "resultJson"),
    generatedAt: requiredString(row, "generatedAt"),
  });
}

function parseJson<T>(value: unknown, schema: z.ZodType<T>, field: string): T {
  if (typeof value !== "string") {
    throw new AdapterDatabaseError(`D1 field ${field} is not JSON text`);
  }

  try {
    return schema.parse(JSON.parse(value));
  } catch (error) {
    throw new AdapterDatabaseError(`D1 field ${field} contains invalid JSON`, { cause: error });
  }
}

function requiredString(row: Record<string, unknown>, field: string): string {
  const value = row[field];
  if (typeof value !== "string" || value.length === 0) {
    throw new AdapterDatabaseError(`D1 field ${field} must be a non-empty string`);
  }
  return value;
}

function nullableString(row: Record<string, unknown>, field: string): string | null {
  const value = row[field];
  if (value === null || value === undefined) return null;
  if (typeof value !== "string") {
    throw new AdapterDatabaseError(`D1 field ${field} must be a string or null`);
  }
  return value;
}

function requiredNumber(row: Record<string, unknown>, field: string): number {
  const value = row[field];
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new AdapterDatabaseError(`D1 field ${field} must be a finite number`);
  }
  return value;
}

function nullableNumber(row: Record<string, unknown>, field: string): number | null {
  const value = row[field];
  if (value === null || value === undefined) return null;
  if (typeof value !== "number" || !Number.isFinite(value)) {
    throw new AdapterDatabaseError(`D1 field ${field} must be a finite number or null`);
  }
  return value;
}

function requiredBoolean(row: Record<string, unknown>, field: string): boolean {
  const value = row[field];
  if (value === true || value === 1) return true;
  if (value === false || value === 0) return false;
  throw new AdapterDatabaseError(`D1 field ${field} must be boolean-compatible`);
}

function maximumNullableTimestamp(left: string | null, right: string | null): string | null {
  if (!left) return right;
  if (!right) return left;
  return Date.parse(left) >= Date.parse(right) ? left : right;
}

function assertD1BindLimit(count: number): void {
  if (count > D1_MAX_BOUND_PARAMETERS) {
    throw new AdapterConfigurationError(
      `D1 query requires ${count} bound parameters; maximum is ${D1_MAX_BOUND_PARAMETERS}`,
    );
  }
}

function assertProductionSources<T extends { readonly source: string }>(
  records: readonly T[],
  expectedSourceId: string,
  recordLabel: string,
): void {
  for (const record of records) {
    if (record.source !== expectedSourceId || isSyntheticSourceId(record.source)) {
      throw new AdapterDatabaseError(
        `Production ${recordLabel} source ${record.source} does not match ${expectedSourceId}`,
      );
    }
  }
}

function assertSnapshotHasSufficientSample(
  snapshot: AnalysisSnapshot,
  minimumSample: number,
): void {
  if (!Number.isInteger(minimumSample) || minimumSample <= 0) {
    throw new AdapterDatabaseError("Analysis config minimumSample must be a positive integer");
  }
  if (snapshot.sampleSize >= minimumSample) return;

  for (const [field, metric] of [
    ["adf", snapshot.adf],
    ["cointegration", snapshot.cointegration],
    ["halfLife", snapshot.halfLife],
  ] as const) {
    if (metric.status === "available") {
      throw new AdapterDatabaseError(
        `Snapshot ${field} cannot be available with ${snapshot.sampleSize} observations; config requires ${minimumSample}`,
      );
    }
  }
}
